import React, { useEffect, useRef } from "react";
import { Outlet, useLocation, useNavigate } from "react-router-dom";
import Header from "../components/Header";
import RouteTabs from "../tabs/RouteTab";
import { useTabs } from "../tabs/TabContext";
import { PROTECTED_ROUTES, KEEP_ALIVE_EXCLUDE } from "../routes/protectedRoutes";
import { TabPathContext } from "../tabs/TabActivation";

// ✅ path → element 조회용 맵 + 블랙리스트 차감 — routes/protectedRoutes.js가
// 라우팅과 keep-alive 후보의 단일 소스이므로, 새 페이지를 추가해도 여기 등록이
// 누락될 수 없다(라우트가 없으면 애초에 페이지 자체가 안 열림).
const KEEP_ALIVE_MAP = Object.fromEntries(
  PROTECTED_ROUTES.filter((r) => !KEEP_ALIVE_EXCLUDE.includes(r.path)).map((r) => [r.path, r.element])
);

const TITLE_MAP = {
  "/": "메인",

  "/mypage": "마이페이지",
  "/dashboard": "대시보드(메인화면)",
  "/expense/management": "경영팀 지출결의서",

  // ✅ 업무 관리 ======================================================================
  "/task/assign/new":    "[alt + 2] 업무 요청",
  "/task/assign/list":   "내가 지시한 업무 목록",
  "/task/received/list": "[alt + 3] 받은 업무",
  "/task/by-employee":   "직원별 업무 목록",
  "/task/chat":          "읽지 않은 업무",

  // ✅ 업무성과 ======================================================================
  "/work-performance/quick":   "[alt + 1] 빠른 입력",
  "/work-performance/summary": "업무처리 현황",
  "/work-performance/dashboard": "업무 성과 대시보드",

  // ✅ 교육 관리 ======================================================================
  "/edu/job":     "직무교육",
  "/edu/youtube": "유튜브 강의",
  "/edu/legal":   "법정의무교육(현장근로자)",
  "/edu/cert":    "개인별 수료증 관리(사무직원)",




  // ✅ 전자결재 ======================================================================
  "/approval/draftdocument/new": "지출기안 작성", // 지출기안 페이지로 리다이렉트되는 경로 — 탭 이름도 통일
  "/approval/draftdocument/list": "기안서 목록",
  "/approval/expense-draft/new": "지출기안 작성",
  "/approval/income-draft/new": "수입기안 작성",
  "/approval/income-resolution/new": "수입결의 작성",
  "/approval/expense-resolution/new": "지출결의 작성",
  "/approval/inbox":              "수신결재",           // ✅ 추가
  "/approval/done":               "결재완료",           // ✅ 추가
  "/approval/inprogress":         "결재진행중",         // ✅ 추가
  "/approval/cc":                 "수신참조",           // ✅ 추가
  "/approval/leave/new":          "휴가신청서 작성",
  "/approval/leave/list":         "휴가신청 목록",


  // ✅ 수입업무 ======================================================================


  "/income/business/new": "경영팀 수입 건",
  "/income/business/list": "경영팀 수입목록",

  "/income/service/new": "용역사업 수입 건",
  "/income/service/list": "용역사업 수입목록",

  "/income/cafe/gasan/new": "CAFE_INN 가산점 수입 건",
  "/income/cafe/gasan/list": "CAFE_INN 가산점 수입목록", 

  "/income/cafe/doksan/new": "CAFE_INN 독산점 수입 건",
  "/income/cafe/doksan/list": "CAFE_INN 독산점 수입목록",   

  "/income/cafe/geumnarae/new": "CAFE_INN 금나래점 수입 건",
  "/income/cafe/geumnarae/list": "CAFE_INN 금나래점 수입목록", 

  "/income/cafe/green/new": "CAFE_INN 그린점 수입 건",
  "/income/cafe/green/list": "CAFE_INN 그린점 수입목록",


  "/income/env/disinfection/new": "환경/소독 수입 건",
  "/income/env/disinfection/list": "환경/소독 수입목록",

  "/income/env/total/new": "환경/종합 수입 건",
  "/income/env/total/list": "환경/종합 수입목록",

  // ✅ 수입데이터+수입기안 통합 작성(신규) ============================================
  "/income/business/draft/new": "수입기안 - 경영팀",
  "/income/business/draft/list": "수입목록 - 경영팀",

  "/income/env/total/draft/new": "수입기안 - 종합",
  "/income/env/total/draft/list": "수입목록 - 종합",

  "/income/env/disinfection/draft/new": "수입기안 - 소독",
  "/income/env/disinfection/draft/list": "수입목록 - 소독",

  "/income/service/draft/new": "수입기안 - 용역",
  "/income/service/draft/list": "수입목록 - 용역",

  // ✅ 지출업무 ======================================================================
  "/expense/business/new": "(구) 경영팀 지출 건",
  "/expense/business/list": "(구) 경영팀 지출목록",
  "/expense/business/draft/new": "경영팀 지출데이터+기안 작성",
  "/expense/business/draft/list": "경영팀 지출목록",
  
  "/expense/service/new": "(구) 용역사업 지출 건",
  "/expense/service/list": "(구) 용역사업 지출목록",
  "/expense/service/draft/new": "용역사업 지출데이터+기안 작성",
  "/expense/service/draft/list": "용역사업 지출목록",

  "/expense/cafe/gasan/new": "(구) CAFE_INN 가산점 지출 건",
  "/expense/cafe/gasan/list": "(구) CAFE_INN 가산점 지출목록",
  "/expense/cafe/gasan/draft/new": "카페인 가산점 지출데이터+기안 작성",
  "/expense/cafe/gasan/draft/list": "카페인 가산점 지출목록",

  "/expense/cafe/doksan/new": "(구) CAFE_INN 독산점 지출 건",
  "/expense/cafe/doksan/list": "(구) CAFE_INN 독산점 지출목록",
  "/expense/cafe/doksan/draft/new": "카페인 독산점 지출데이터+기안 작성",
  "/expense/cafe/doksan/draft/list": "카페인 독산점 지출목록",

  "/expense/cafe/geumnarae/new": "(구) CAFE_INN 금나래점 지출 건",
  "/expense/cafe/geumnarae/list": "(구) CAFE_INN 금나래점 지출 목록",
  "/expense/cafe/geumnarae/draft/new": "카페인 금나래점 지출데이터+기안 작성",
  "/expense/cafe/geumnarae/draft/list": "카페인 금나래점 지출목록",

  "/expense/cafe/green/new": "(구) CAFE_INN 그린점 지출 건",
  "/expense/cafe/green/list": "(구) CAFE_INN 그린점 지출목록",
  "/expense/cafe/green/draft/new": "카페인 그린점 지출데이터+기안 작성",
  "/expense/cafe/green/draft/list": "카페인 그린점 지출목록",
  
  "/expense/env/disinfection/new": "(구) 환경/소독 지출 건",
  "/expense/env/disinfection/list": "(구) 환경/소독 지출목록",
  "/expense/env/disinfection/draft/new": "환경/소독 지출데이터+기안 작성",
  "/expense/env/disinfection/draft/list": "환경/소독 지출목록",

  "/expense/env/total/new": "(구) 환경/종합 지출 건",
  "/expense/env/total/list": "(구) 환경/종합 지출목록",
  "/expense/env/total/draft/new": "환경/종합 지출데이터+기안 작성",
  "/expense/env/total/draft/list": "환경/종합 지출목록",


  // ✅ 회계업무 ======================================================================

  "/cafe/expense-ledger": "카페 지출 기안 대장",
  "/accounting/general/sales/new":    "(일반) 매출자료 작성", 
  "/accounting/general/sales/list":   "(일반) 매출자료 조회",
  "/accounting/accounts/sales/new":   "(일반) 매출 계정과목 등록",
  "/accounting/accounts/sales-list":  "(일반) 매출 계정과목 목록",
  "/accounting/budget/yearly/list":    "연도별 예산서 목록",
  "/accounting/budget/yearly/new":     "연도별 예산서 업로드",
  "/accounting/bank/register":        "통장 등록",

  // ✅ 기성계 자동화 ======================================================================
  "/gisung-gye/contracts": "기성계약 등록/관리",
  "/gisung-gye/guchung":   "기성계 생성 (구청 등)",
  "/gisung-gye/gongdan":   "기성계 생성 (공단)",
  "/gisung-gye/history":   "기성 생성 이력",

  // ✅ 용역 견적서 관리 ====================================================================
  "/quotation/list": "견적서 목록",
  "/quotation/new":  "견적서 작성",


  // ✅ 인사평가 ================================================================
  "/hreval/list": "일용직 인사평가",   
  "/hreval/contract": "계약직 인사평가", 

  
  // ✅ 경영평가 ================================================================
  "/evaluation/manuals": "경영평가 편람",

  // ✅ 팀장 사용설명서 ==========================================================
  "/manual-guide": "팀장 사용설명서",
  

  // ✅ 기타업무 ======================================================================
  "/etc/staff-contacts": "임직원 연락망",
  "/contacts": "임직원 연락망",
  "/etc/vendors": "거래처 관리",
  "/etc/websites": "ID/PW",                  

  "/etc/expense-usage-codes": "지출 사용내역 코드 관리",
  "/etc/mail-alert": "공용메일 알림",

  // ✅ 금천아 빌려줘 (카페24 대여 주문)
  "/rental/orders": "금천아 빌려줘",
  "/rental/sales": "빌려줘 매출 현황",

  // ✅ 에어컨 청소 예약(홈페이지 gc-job.co.kr 연동)
  "/aircon/reservations": "에어컨 청소 예약",
  
  "/etc/calendar/general": "[alt + `] 캘린더(일반)",
  "/etc/calendar/admin": "캘린더(관리자)",
  "/etc/holiday/manage": "공휴일(관리자)",
  "/work-schedule": "업무일정표",




  "/etc/income-status": "금천일자리(주) 수입 현황",
  "/etc/expense-status": "금천일자리(주) 지출 현황",   
  "/etc/total-status":   "금천일자리(주) 수입/지출 현황",
  "/etc/insurance-allocation": "사회보험 부문 구분",
  "/etc/insurance-annual": "개인별 연간 사회보험료 합계",   // ✅ 추가

  "/payroll/tax-table": "근로소득 간이세액표",
  "/payroll/wage-rates": "시급 설정",   
  "/payroll/sheet": "급여명세서",   

  
  "/welfare/point": "복지포인트",              

  "/daily-todo": "오늘의 주요 업무",
  "/daily-todo/meeting": "팀 회의 모드",
  "/daily-todo/stats": "업무 완료율 통계",

};

const HIDE_CHROME_PATHS = ["/", "/login", "/signup"];

function fallbackTitleFrom(pathname) {
  // 동적 경로 패턴 처리
  if (/^\/accounting\/budget\/yearly\/\d+$/.test(pathname)) return "연도별 예산서 상세";
  if (/^\/approval\/draftdocument\/\d+$/.test(pathname)) return "기안서 상세";  // ← 추가
  if (/^\/evaluation\/manuals\/\d+\/viewer$/.test(pathname)) return "경영평가 편람 미리보기";
  if (/^\/evaluation\/manuals\/\d+\/indicators$/.test(pathname)) return "경영평가 지표 보기";
  // 대여 주문 상세 — 이름을 아직 모르는 시점(딥링크·새로고침)의 폴백.
  // 주문을 읽은 뒤 RentalOrderDetail이 "빌려줘 · 수령인"으로 갱신한다.
  if (/^\/rental\/orders\/\d+$/.test(pathname)) return "빌려줘 상세";

  const parts = pathname.split("/").filter(Boolean);
  const last = parts.pop();
  if (!last) return "메인";
  return last.replace(/[-_]+/g, " ").replace(/\b\w/g, (ch) => ch.toUpperCase());
}
export default function AppLayout() {

  const loc = useLocation(); // ✅ 이름 변경
  const navigate = useNavigate(); // ✅ 추가
  const { state, dispatch } = useTabs();

  useEffect(() => {
    const title =
      loc.state?.tabTitle || TITLE_MAP[loc.pathname] || fallbackTitleFrom(loc.pathname);

    dispatch({
      type: "ADD_or_ACTIVATE",
      payload: { path: loc.pathname, title, icon: "📄" },
    });
  }, [loc.pathname, loc.state, dispatch]);

  // ✅ Keep-alive 탭 — KEEP_ALIVE_MAP(=전체 정적 라우트 - 블랙리스트)에 있는 경로는
  // <Outlet/>이 아니라 여기서 직접 렌더하고 display:none으로만 숨긴다(탭 전환 시
  // 언마운트되지 않게). keepAliveVisitedRef는 "이번 세션에서 실제로 방문한" 경로만
  // 누적한다 — 탭 목록(state.tabs)은 localStorage에 저장돼 새로고침 후에도 남아있어,
  // 그걸 그대로 기준 삼으면 앱을 열자마자 예전에 열려있던 탭들을 전부 백그라운드
  // 마운트해버린다(불필요한 API 호출 등, 특히 탭이 많이 쌓인 세션에서 부담).
  // 탭이 닫히면(state.tabs에서 빠지면) 아래 keptAlivePaths 필터링에서 자연히 제외돼
  // 언마운트된다.
  const keepAliveVisitedRef = useRef(new Set());
  useEffect(() => {
    if (KEEP_ALIVE_MAP[loc.pathname]) keepAliveVisitedRef.current.add(loc.pathname);
  }, [loc.pathname]);

  const isCurrentKeptAlive = !!KEEP_ALIVE_MAP[loc.pathname];
  const openTabPaths = new Set(state.tabs.map((t) => t.path));
  // 현재 경로는 위 effect가 아직 안 돌았어도(직접 URL 진입 등) 항상 포함시켜
  // 첫 렌더에서 빈 화면이 잠깐 보이는 걸 방지한다.
  const keptAlivePaths = [...new Set([...keepAliveVisitedRef.current, loc.pathname])].filter(
    (p) => KEEP_ALIVE_MAP[p] && (p === loc.pathname || openTabPaths.has(p))
  );

  useEffect(() => {

    console.log("[전역 단축키] Ctrl + Q 등록 완료");
  
    const handleGlobalShortcut = (event) => {
  
      console.log("[키 입력 감지]", {
        key: event.key,
        ctrlKey: event.ctrlKey,
        target: event.target
      });
  
      // Alt + ` (캘린더 일반)
      if (event.altKey && !event.ctrlKey && event.key === "`") {
        event.preventDefault();
        navigate("/etc/calendar/general");
      }
      
      // Alt + 1 (업무 빠른입력)
      if (event.altKey && !event.ctrlKey && event.key === "1") {
        event.preventDefault();
        navigate("/work-performance/quick");
      }

      // Alt + 2 (업무 지시 작성)
      if (event.altKey && !event.ctrlKey && event.key === "2") {
        event.preventDefault();
        navigate("/task/assign/new");
      }

      // Alt + 3 (받은 업무 목록)
      if (event.altKey && !event.ctrlKey && event.key === "3") {
        event.preventDefault();
        navigate("/task/received/list");
      }

    };
  
    // 전역 이벤트 등록
    window.addEventListener("keydown", handleGlobalShortcut);
  
    console.log("[keydown 이벤트 등록 완료]");
  
    // 제거
    return () => {
  
      console.log("[keydown 이벤트 제거]");
  
      window.removeEventListener("keydown", handleGlobalShortcut);
    };
  
  }, [navigate]);

  const hide = HIDE_CHROME_PATHS.includes(loc.pathname);

  return (
    <div className="app-shell">
      {!hide && <Header />}
      <div className="container" style={{ padding: "12px 16px" }}>
        {!hide && <RouteTabs />}
        {/* keep-alive 등록 경로가 아니면 기존 그대로 <Outlet/>이 렌더 — 다른 페이지는
            전혀 영향받지 않는다. */}
        {!isCurrentKeptAlive && <Outlet />}
        {keptAlivePaths.map((path) => (
          // ✅ TabPathContext — 이 서브트리가 "대표하는 경로"를 알려줘서 안의
          // 페이지가 useTabActivated()로 재방문(display:none→block 전환)을 감지할 수
          // 있게 한다(자세한 설명은 tabs/TabActivation.js 참고).
          <TabPathContext.Provider key={path} value={path}>
            <div style={{ display: path === loc.pathname ? "block" : "none" }}>
              {KEEP_ALIVE_MAP[path]}
            </div>
          </TabPathContext.Provider>
        ))}
      </div>
    </div>
  );
}
